"use client";

import Image from "next/image";
import { useState } from "react";

import { useImageFade } from "./use-image-fade";

/**
 * Detail-page gallery: a large main frame over a thumbnail filmstrip. Hovering
 * a thumb previews it in the main area; clicking pins it as the default. The
 * pinned thumb is marked with a black corner triangle (the only indicator —
 * the strip doesn't dim). Panels are borderless bg-muted squares with
 * true 2px gaps (same look as the catalog grid); the strip is an explicit grid
 * with minmax(0,1fr) columns so widths stay even (flex-1 + aspect-square
 * rounds unevenly in Safari iOS). object-contain so the whole product reads.
 */
export function ProductGallery({
  images,
  alt,
}: {
  images: string[];
  alt: string;
}) {
  // `pinned` is the click-locked image; `hovered` previews on top of it.
  const [pinned, setPinned] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  // Fade the gallery in once the main image first loads; thumb hover/click
  // swaps the main image instantly within the already-revealed frame.
  const { loaded, ref, onLoad } = useImageFade();
  if (images.length === 0) return null;

  const shown = hovered ?? pinned;

  return (
    // One grid for the whole stack so the main↔strip gap uses the same grid
    // `gap` as the thumbs (Safari collapses a plain margin-top here).
    <div
      className={`grid grid-cols-1 gap-[2px] transition-opacity duration-700 ease-out ${
        loaded ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="relative aspect-square overflow-hidden bg-bg-muted">
        <Image
          key={images[shown]}
          ref={ref}
          src={images[shown]}
          alt={alt}
          fill
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="object-contain"
          unoptimized
          priority
          onLoad={onLoad}
        />
      </div>
      {images.length > 1 && (
        <div
          className="grid gap-[2px]"
          style={{
            gridTemplateColumns: `repeat(${images.length}, minmax(0, 1fr))`,
          }}
          onMouseLeave={() => setHovered(null)}
        >
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onMouseEnter={() => setHovered(i)}
              onFocus={() => setHovered(i)}
              onBlur={() => setHovered(null)}
              onClick={() => setPinned(i)}
              aria-label={`View image ${i + 1}`}
              aria-pressed={pinned === i}
              className="relative aspect-square cursor-pointer overflow-hidden bg-bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-fg"
            >
              <Image
                src={src}
                alt=""
                fill
                sizes="160px"
                className="object-contain"
                unoptimized
              />
              {pinned === i && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute top-0 right-0 z-10 h-0 w-0 border-t-[14px] border-l-[14px] border-t-black border-l-transparent"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
